import { DefaultArtifactClient } from '@actions/artifact'
import fs from 'fs/promises'
import path from 'path'
import { BaseStorageProvider } from './base.provider.js'
import type { FileToUpload, UploadedFile, DownloadedFile, GitHubConfig } from '../types.js'
import { getFileSize, getTimestamp, sanitizePath } from '../utils.js'

/**
 * GitHub Actions Artifacts storage provider
 */
export class GitHubStorageProvider extends BaseStorageProvider {
  readonly name = 'github' as const
  private client: DefaultArtifactClient
  private config: Required<Omit<GitHubConfig, 'basePath'>> & Pick<GitHubConfig, 'basePath'>
  private tempDir: string = ''
  private artifactId?: string

  constructor(config?: GitHubConfig, verbose: boolean = false) {
    super(verbose)
    this.client = new DefaultArtifactClient()
    this.config = {
      artifactName: config?.artifactName || 'storage-artifacts',
      retentionDays: config?.retentionDays || 90,
      basePath: config?.basePath
    }
  }

  async initialize(): Promise<void> {
    // Verify we're running in GitHub Actions
    if (!process.env.GITHUB_ACTIONS) {
      throw new Error('GitHub Actions provider can only be used within GitHub Actions environment')
    }

    // Create temp directory for staging files
    this.tempDir = path.join(process.cwd(), '.storage-temp', Date.now().toString())
    await fs.mkdir(this.tempDir, { recursive: true })

    this.log('Initialized GitHub Actions Artifacts provider')
  }

  async uploadFile(file: FileToUpload): Promise<UploadedFile> {
    const localPath = file.path
    const size = await getFileSize(localPath)

    // Copy file to temp directory with destination name
    const destination = file.destination || path.basename(localPath)
    const remotePath = this.config.basePath
      ? path.join(this.config.basePath, destination)
      : destination
    const tempPath = path.join(this.tempDir, sanitizePath(remotePath))

    // Ensure subdirectories exist
    await fs.mkdir(path.dirname(tempPath), { recursive: true })
    await fs.copyFile(localPath, tempPath)

    return {
      localPath,
      remotePath,
      size,
      contentType: file.contentType,
      uploadedAt: getTimestamp()
    }
  }

  async uploadFiles(files: FileToUpload[]): Promise<UploadedFile[]> {
    const results: UploadedFile[] = []

    // Stage all files to temp directory first
    for (const file of files) {
      const result = await this.uploadFile(file)
      results.push(result)
    }

    // Upload the entire temp directory as a single artifact
    try {
      this.log(`Uploading artifact '${this.config.artifactName}' with ${files.length} files...`)

      const response = await this.client.uploadArtifact(
        this.config.artifactName,
        [this.tempDir],
        process.cwd(),
        {
          retentionDays: this.config.retentionDays
        }
      )

      // Store artifact ID if available (may not exist in all versions)
      this.artifactId = (response as any).id || (response as any).artifactId

      this.log(`Successfully uploaded artifact: ${this.config.artifactName}`)
      return results
    } catch (error) {
      this.logError('Failed to upload artifact', error as Error)
      throw error
    }
  }

  async downloadFile(_remotePath: string): Promise<DownloadedFile> {
    throw new Error('GitHub Artifacts do not support individual file downloads. Use downloadArtifact to download entire artifacts.')
  }

  async fileExists(_remotePath: string): Promise<boolean> {
    // GitHub Artifacts don't support individual file checks
    // Would need to download and check
    return false
  }

  async deleteFile(_remotePath: string): Promise<void> {
    throw new Error('GitHub Artifacts do not support individual file deletion')
  }

  async listFiles(_remotePath?: string): Promise<string[]> {
    throw new Error('GitHub Artifacts do not support file listing')
  }

  async cleanup(): Promise<void> {
    // Clean up temp directory
    if (this.tempDir) {
      try {
        await fs.rm(this.tempDir, { recursive: true, force: true })
        this.log('Cleaned up temporary directory')
      } catch (error) {
        this.logError('Failed to clean up temp directory', error as Error)
      }
    }
  }

  /**
   * Get artifact ID if upload was successful
   */
  getArtifactId(): string | undefined {
    return this.artifactId
  }

  /**
   * Get artifact name
   */
  getArtifactName(): string {
    return this.config.artifactName
  }
}
